import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  collection, onSnapshot, doc,
  updateDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import './MembersPage.css';

const ALL = 'All';

const KERALA_DISTRICTS = [
  'Thiruvananthapuram', 'Kollam', 'Pathanamthitta', 'Alappuzha',
  'Kottayam', 'Idukki', 'Ernakulam', 'Thrissur', 'Palakkad',
  'Malappuram', 'Calicut', 'Wayanad', 'Kannur', 'Kasaragod',
];
const FRONX_VARIANTS = [
  'Sigma', 'Delta', 'Delta+', 'Delta+ O', 'Zeta', 'Alpha',
];
const FRONX_COLORS = [
  'Nexa Blue', 'Splendid Silver', 'Grandeur Grey', 'Opulent Red',
  'Arctic White', 'Earthen Brown', 'Cafe Noir (Dual Tone)',
  'Bluish Black (Dual Tone)',
];
const BLOOD_GROUPS = [
  'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown',
];
const ENGINE_VARIANTS = [
  '1.2L Petrol Manual', '1.2L Petrol Automatic',
  '1.2L CNG Manual',    '1.2L CNG Automatic',
  '1.0L Turbo Petrol Manual', '1.0L Turbo Petrol Automatic',
];

export default function MembersPage() {
  const [members, setMembers]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search,  setSearch]    = useState('');
  const [district, setDistrict] = useState(ALL);
  const [status,   setStatus]   = useState(ALL);
  const [toast,    setToast]    = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // member to confirm-delete
  const [editTarget,   setEditTarget]   = useState(null); // member to edit

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'allowed_users'),
      (snap) => {
        setMembers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  const districts = useMemo(() => {
    const set = new Set(members.map(m => m.district).filter(Boolean));
    return [ALL, ...Array.from(set).sort()];
  }, [members]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter(m => {
      const matchSearch = !q
        || m.name?.toLowerCase().includes(q)
        || m.phone_number?.includes(q)
        || m.member_id?.toLowerCase().includes(q);
      const matchDistrict = district === ALL || m.district === district;
      const matchStatus   = status   === ALL || m.status   === status;
      return matchSearch && matchDistrict && matchStatus;
    });
  }, [members, search, district, status]);

  const toggleStatus = useCallback(async (member) => {
    const newStatus = member.status === 'active' ? 'inactive' : 'active';
    try {
      await updateDoc(doc(db, 'allowed_users', member.id), {
        status:     newStatus,
        updated_at: serverTimestamp(),
      });
      showToast(`${member.name} marked as ${newStatus}.`, 'success');
    } catch (e) {
      showToast(`Failed to update status: ${e.message}`, 'error');
    }
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(db, 'allowed_users', deleteTarget.id));
      showToast(`${deleteTarget.name} removed.`, 'success');
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Renew membership: bump expiry by 1 year from today (or from
  //    current expiry if it's still in the future).
  const renewMembership = useCallback(async (member) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const base = member.membership_expiry && member.membership_expiry > todayStr
      ? member.membership_expiry
      : todayStr;
    const d = new Date(base);
    d.setFullYear(d.getFullYear() + 1);
    const newExpiry = d.toISOString().slice(0, 10);

    const ok = window.confirm(
      `Renew ${member.name}'s membership until ${newExpiry}?`
    );
    if (!ok) return;

    try {
      await updateDoc(doc(db, 'allowed_users', member.id), {
        membership_expiry: newExpiry,
        status:            'active',
        updated_at:        serverTimestamp(),
      });
      showToast(`${member.name} renewed until ${newExpiry}.`, 'success');
    } catch (e) {
      showToast(`Renew failed: ${e.message}`, 'error');
    }
  }, []);

  const saveEdits = useCallback(async (updated) => {
    try {
      await updateDoc(doc(db, 'allowed_users', editTarget.id), {
        ...updated,
        updated_at: serverTimestamp(),
      });
      showToast(`${updated.name || editTarget.name} updated.`, 'success');
      setEditTarget(null);
    } catch (e) {
      showToast(`Update failed: ${e.message}`, 'error');
    }
  }, [editTarget]);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Members</h1>
          <p className="page-subtitle">
            {members.length} total · {members.filter(m => m.status === 'active').length} active
          </p>
        </div>
        <Link to="/members/add" className="mem-add-btn">+ Add Member</Link>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`mem-toast mem-toast--${toast.type}`}>
          {toast.message}
        </div>
      )}

      {/* Filters */}
      <div className="mem-filters">
        <input
          type="search"
          className="mem-search"
          placeholder="Search name, phone, member ID…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="mem-select"
          value={district}
          onChange={e => setDistrict(e.target.value)}
        >
          {districts.map(d => <option key={d}>{d}</option>)}
        </select>
        <select
          className="mem-select"
          value={status}
          onChange={e => setStatus(e.target.value)}
        >
          {[ALL, 'active', 'inactive'].map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="mem-loading"><span className="tf-spinner tf-spinner--red" /></div>
      ) : filtered.length === 0 ? (
        <div className="mem-empty">No members match this filter.</div>
      ) : (
        <div className="mem-table-wrap">
          <table className="mem-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>ID</th>
                <th>District</th>
                <th>Variant</th>
                <th>Colour</th>
                <th>Expiry</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <MemberRow
                  key={m.id}
                  member={m}
                  onToggle={() => toggleStatus(m)}
                  onEdit={() => setEditTarget(m)}
                  onRenew={() => renewMembership(m)}
                  onDelete={() => setDeleteTarget(m)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <DeleteDialog
          member={deleteTarget}
          onConfirm={confirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      {/* Edit dialog */}
      {editTarget && (
        <EditDialog
          member={editTarget}
          onSave={saveEdits}
          onCancel={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}

function expiryInfo(expiry) {
  if (!expiry) return { label: '—', color: '#B79F6D' };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const exp   = new Date(expiry);
  const days  = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  if (days < 0)  return { label: `Expired ${expiry}`,    color: '#E63946' };
  if (days <= 14) return { label: `${expiry} (${days}d)`, color: '#E63946' };
  if (days <= 60) return { label: `${expiry} (${days}d)`, color: '#D4B574' };
  return { label: expiry, color: '#22C55E' };
}

function MemberRow({ member: m, onToggle, onEdit, onRenew, onDelete }) {
  const [toggling, setToggling] = useState(false);
  const exp = expiryInfo(m.membership_expiry);

  const handleToggle = async () => {
    setToggling(true);
    await onToggle();
    setToggling(false);
  };

  return (
    <tr>
      <td>
        <div className="mem-name-cell">
          {m.profile_photo_url ? (
            <img
              src={m.profile_photo_url}
              alt={m.name}
              className="mem-avatar"
              style={{ objectFit: 'cover' }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="mem-avatar">
              {m.name?.[0]?.toUpperCase() ?? '?'}
            </div>
          )}
          <div>
            <div className="mem-name">{m.name}</div>
            <div className="mem-phone">{m.phone_number}</div>
          </div>
        </div>
      </td>
      <td><code className="mem-id">{m.member_id}</code></td>
      <td>{m.district}</td>
      <td>{m.car_variant}</td>
      <td>{m.car_color}</td>
      <td style={{ color: exp.color, fontSize: 12, fontWeight: 600 }}>
        {exp.label}
      </td>
      <td>
        <button
          className={`mem-status-btn mem-status-btn--${m.status}`}
          onClick={handleToggle}
          disabled={toggling}
          title={`Click to mark ${m.status === 'active' ? 'inactive' : 'active'}`}
        >
          {toggling ? <span className="tf-spinner tf-spinner--sm" /> : m.status}
        </button>
      </td>
      <td>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={onRenew}
            title="Renew membership for 1 year"
            style={{
              padding: '6px 10px',
              background: '#22C55E',
              border: '1px solid #22C55E',
              borderRadius: 6,
              color: '#000',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Renew
          </button>
          <button
            onClick={onEdit}
            title="Edit member"
            style={{
              padding: '6px 10px',
              background: 'transparent',
              border: '1px solid #D4B574',
              borderRadius: 6,
              color: '#D4B574',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Edit
          </button>
          <button
            className="mem-delete-btn"
            onClick={onDelete}
            title="Remove member"
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
}

function EditDialog({ member, onSave, onCancel }) {
  const [form, setForm] = useState({
    name:              member.name              ?? '',
    district:          member.district          ?? '',
    car_variant:       member.car_variant       ?? '',
    car_color:         member.car_color         ?? '',
    vehicle_number:    member.vehicle_number    ?? '',
    blood_group:       member.blood_group       ?? '',
    emergency_contact: member.emergency_contact ?? '',
    engine_variant:    member.engine_variant    ?? '',
    membership_expiry: member.membership_expiry ?? '',
    status:            member.status            ?? 'active',
  });
  const [saving, setSaving] = useState(false);

  const change = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave({
      ...form,
      vehicle_number: form.vehicle_number.trim().toUpperCase(),
      name:           form.name.trim(),
    });
    setSaving(false);
  };

  return (
    <div className="mem-dialog-overlay" onClick={onCancel}>
      <div
        className="mem-dialog"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h3 className="mem-dialog-title">
          Edit Member · <code style={{ color: '#D4B574' }}>{member.member_id}</code>
        </h3>

        <div style={{
          fontSize: 11,
          color: '#B79F6D',
          marginBottom: 16,
          fontFamily: 'monospace',
        }}>
          {member.phone_number} <span style={{ opacity: 0.6 }}>(phone — read-only)</span>
        </div>

        <EditField label="Full Name">
          <input className="amf-input" name="name"
                 value={form.name} onChange={change} />
        </EditField>

        <EditField label="District">
          <select className="amf-select" name="district"
                  value={form.district} onChange={change}>
            <option value="">— Select —</option>
            {KERALA_DISTRICTS.map(d => <option key={d}>{d}</option>)}
          </select>
        </EditField>

        <EditField label="Vehicle Number">
          <input className="amf-input" name="vehicle_number"
                 style={{ textTransform: 'uppercase' }}
                 value={form.vehicle_number} onChange={change} />
        </EditField>

        <EditField label="Fronx Variant">
          <select className="amf-select" name="car_variant"
                  value={form.car_variant} onChange={change}>
            <option value="">— Select —</option>
            {FRONX_VARIANTS.map(v => <option key={v}>{v}</option>)}
          </select>
        </EditField>

        <EditField label="Engine Variant">
          <select className="amf-select" name="engine_variant"
                  value={form.engine_variant} onChange={change}>
            <option value="">— Select —</option>
            {ENGINE_VARIANTS.map(v => <option key={v}>{v}</option>)}
          </select>
        </EditField>

        <EditField label="Car Colour">
          <select className="amf-select" name="car_color"
                  value={form.car_color} onChange={change}>
            <option value="">— Select —</option>
            {FRONX_COLORS.map(c => <option key={c}>{c}</option>)}
          </select>
        </EditField>

        <EditField label="Blood Group">
          <select className="amf-select" name="blood_group"
                  value={form.blood_group} onChange={change}>
            <option value="">— Select —</option>
            {BLOOD_GROUPS.map(b => <option key={b}>{b}</option>)}
          </select>
        </EditField>

        <EditField label="Emergency Contact">
          <input className="amf-input" name="emergency_contact" type="tel"
                 value={form.emergency_contact} onChange={change} />
        </EditField>

        <EditField label="Membership Expiry">
          <input className="amf-input" name="membership_expiry" type="date"
                 value={form.membership_expiry} onChange={change} />
        </EditField>

        <EditField label="Status">
          <select className="amf-select" name="status"
                  value={form.status} onChange={change}>
            <option value="active">active</option>
            <option value="inactive">inactive</option>
          </select>
        </EditField>

        <div className="mem-dialog-actions" style={{ marginTop: 20 }}>
          <button className="mem-dialog-cancel" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            className="mem-dialog-confirm"
            onClick={handleSave}
            disabled={saving}
            style={{ background: '#D4B574', color: '#000' }}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditField({ label, children }) {
  return (
    <div className="amf-field" style={{ marginBottom: 12 }}>
      <label className="amf-label" style={{ fontSize: 11 }}>{label}</label>
      {children}
    </div>
  );
}

function DeleteDialog({ member, onConfirm, onCancel }) {
  return (
    <div className="mem-dialog-overlay" onClick={onCancel}>
      <div className="mem-dialog" onClick={e => e.stopPropagation()}>
        <h3 className="mem-dialog-title">Remove Member?</h3>
        <p className="mem-dialog-body">
          This will permanently delete <strong>{member.name}</strong> ({member.phone_number})
          from <code>allowed_users</code>. They will lose access to the app immediately.
        </p>
        <div className="mem-dialog-actions">
          <button className="mem-dialog-cancel" onClick={onCancel}>Cancel</button>
          <button className="mem-dialog-confirm" onClick={onConfirm}>Remove</button>
        </div>
      </div>
    </div>
  );
}
